
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { SecureAuthProvider } from "@/contexts/SecureAuthContext";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import Settings from "./pages/Settings";
import ChangePassword from "./pages/ChangePassword";
import ResetPassword from "./pages/ResetPassword";
import SharedImage from "./pages/SharedImage";
import TryGenerator from "./pages/TryGenerator";
import CompleteGallery from "./pages/CompleteGallery";
import Gallery from "./pages/Gallery";
import AdminPanel from "./pages/AdminPanel";
import NotFound from "./pages/NotFound";
import HelpCenter from "./pages/HelpCenter";
import ContactUs from "./pages/ContactUs";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Documentation from "./pages/Documentation";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import EnterpriseDemo from "./pages/EnterpriseDemo";
import EcommercePage from "./pages/industries/EcommercePage";
import MarketingPage from "./pages/industries/MarketingPage";
import CreativeAgenciesPage from "./pages/industries/CreativeAgenciesPage";
import PublishingPage from "./pages/industries/PublishingPage";
import PhotographyPage from "./pages/industries/PhotographyPage";
import RealEstatePage from "./pages/industries/RealEstatePage";
import SocialMediaPage from "./pages/industries/SocialMediaPage";
import GamingPage from "./pages/industries/GamingPage";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";
import FAQ from "./pages/FAQ";
import VideoTutorials from "./pages/VideoTutorials";
import CommunityForum from "./pages/CommunityForum";
import About from "./pages/About";
import ScrollToTop from "./components/ScrollToTop";
import MobileTabletNotice from "./components/MobileTabletNotice";
import PromptLibrary from "./pages/PromptLibrary";
import ABTesting from "./pages/ABTesting";
import ABTestingAnalytics from "./pages/ABTestingAnalytics";
import SharedABTest from "./pages/SharedABTest";
import BackgroundQueueRunner from "./components/BackgroundQueueRunner";
import { BackgroundAutomationRunner } from "./components/BackgroundAutomationRunner";
import PublicImageEnhancer from "./pages/PublicImageEnhancer";

const queryClient = new QueryClient();

const AppContent = () => {
  const location = useLocation();
  const showMobileNotice = location.pathname !== '/try-generator';

  return (
    <SecureAuthProvider>
      <ScrollToTop />
      {showMobileNotice && <MobileTabletNotice />}
      <div className="min-h-screen">
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/try-generator" element={<TryGenerator />} />
          <Route path="/prompt-library" element={<PromptLibrary />} />
          <Route path="/ab-testing" element={<ProtectedRoute><ABTesting /></ProtectedRoute>} />
          <Route path="/ab-testing/shared/:token" element={<SharedABTest />} />
          <Route path="/ab-testing/analytics" element={<ProtectedRoute><ABTestingAnalytics /></ProtectedRoute>} />
          <Route path="/ab-testing-analytics" element={<ProtectedRoute><ABTestingAnalytics /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="/change-password" element={<ChangePassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/shared/:shareToken" element={<SharedImage />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/complete-gallery" element={<ProtectedRoute><CompleteGallery /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><AdminRoute><AdminPanel /></AdminRoute></ProtectedRoute>} />
          <Route path="/help" element={<HelpCenter />} />
          <Route path="/help/faq" element={<FAQ />} />
          <Route path="/help/videos" element={<VideoTutorials />} />
          <Route path="/help/community" element={<CommunityForum />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/docs" element={<Documentation />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/enterprise-demo" element={<EnterpriseDemo />} />
          
          {/* Industry Pages */}
          <Route path="/industries/marketing" element={<MarketingPage />} />
          <Route path="/industries/ecommerce" element={<EcommercePage />} />
          <Route path="/industries/photography" element={<PhotographyPage />} />
          <Route path="/industries/real-estate" element={<RealEstatePage />} />
          <Route path="/industries/social-media" element={<SocialMediaPage />} />
          <Route path="/industries/gaming" element={<GamingPage />} />
          <Route path="/industries/creative-agencies" element={<CreativeAgenciesPage />} />
          <Route path="/industries/publishing" element={<PublishingPage />} />
          
          {/* Tools */}
          <Route path="/tools/image-enhancer" element={<PublicImageEnhancer />} />
          
          <Route path="/about" element={<About />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
      <BackgroundQueueRunner />
      <BackgroundAutomationRunner />
      <Toaster />
      <Sonner />
    </SecureAuthProvider>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
